import { Component, Input, OnInit, Self, ViewChildren, QueryList, ViewEncapsulation, EventEmitter, Output } from '@angular/core';
import { DefaultValuesService } from 'src/app/services/defaultvalues.service';
import { EquipmentManagenementService } from 'src/app/services/equipmentmanagement.service';
import { SoftwareReleaseListComponent } from './components/software-release-list/software-release-list.component';
import { forkJoin, map, takeUntil } from 'rxjs';
import { SoftwareReleaseItem } from './types/software-release';
import { EmtAllowedSoftwareReleaseLevelDto, EmtAllowedSoftwareReleaseType } from 'src/app/interfaces/emt/emt-allowed-softwarer-release';
import { DestroyService } from 'src/app/services/destroyservice';

@Component({
    selector: 'softwarereleasetofield',
    templateUrl: './softwarereleasetofield.component.html',
    styleUrls: ['./softwarereleasetofield.component.less', '../../../../style/new-generic-styles.component.less', '../fieldsettings.component.less'],
    providers: [DestroyService],
    standalone: false
})
export class SoftwareReleaseToFieldComponent implements OnInit {
    @ViewChildren(SoftwareReleaseListComponent)
    private readonly lists!: QueryList<SoftwareReleaseListComponent>;

    @Input()
    public availableFamilyCodes: FamilyCode[] = [];
    @Input()
    public familyCodes: Map<number, { familyCode: string; familyCodeName: string }> = new Map();

    public controllerSoftwareReleases: SoftwareReleaseItem[] = [];
    public peripheralSoftwareReleases: SoftwareReleaseItem[] = [];

    @Output()
    public saveSuccess = new EventEmitter<void>();

    constructor(
        private emtService: EquipmentManagenementService,
        public defaults: DefaultValuesService,
        @Self() private readonly destroy$: DestroyService
    ) { }

    public ngOnInit() {
        this.loadSoftwareReleases();
        this.defaults.stageMessageChanges$
            .pipe(takeUntil(this.destroy$))
            .subscribe(() => {
                this.loadSoftwareReleases();
            });
    }

    public onControllerLevelSelected(level: EmtAllowedSoftwareReleaseLevelDto) {
        const controllerList = this.lists?.first;
        if (controllerList) {
            controllerList.changeSelectedLevel(level);
        }
        this.onSoftwareReleaseListSaved();
    }

    public onPeripheralLevelSelected(level: EmtAllowedSoftwareReleaseLevelDto) {
        const peripheralList = this.lists?.last;
        if (peripheralList) {
            peripheralList.changeSelectedLevel(level);
        }
        this.onSoftwareReleaseListSaved();
    }

    private loadSoftwareReleases() {
        const controllerSoftwares$ = this.emtService.getControllerSoftwareReleases().pipe(
            map(response => response?.releasesList ?? []),
            map(list => list.map(x => ({
                id: x.controllerSoftwareVersionID,
                type: EmtAllowedSoftwareReleaseType.Controller,
                version: x.controllerSoftwareVersion,
                releaseName: x.releaseName,
                familyCodeId: x.familyCodeId,
            }) as SoftwareReleaseItem)),
        );

        const peripheralSoftwares$ = this.emtService.getPeriphealSoftwareReleases().pipe(
            map(x => x?.versionList ?? []),
            map(list => list.map(x => ({
                id: x.peripheralSoftwareVersionID,
                type: EmtAllowedSoftwareReleaseType.Peripheral,
                version: x.peripheralSoftwareVersion,
                releaseName: undefined,
                familyCodeId: x.familyCodeId,
            }) as SoftwareReleaseItem)),
        );

        forkJoin([controllerSoftwares$, peripheralSoftwares$])
            .pipe(takeUntil(this.destroy$))
            .subscribe(([controllerSoftwares, peripheralSoftwares]) => {
                this.controllerSoftwareReleases = controllerSoftwares;
                this.peripheralSoftwareReleases = peripheralSoftwares;
            });
    }

    onSoftwareReleaseListSaved() {
        this.saveSuccess.emit();
    }
}